import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// Used when the Date / Advance sections are re-enabled:
// import { useRef } from 'react';
// import { format } from 'date-fns';
// import { Calendar as CalendarIcon, ChevronRight } from 'lucide-react';
// import { MobileSwitch } from '@/components/ui/MobileSwitch';
import { Banknote, Building2, User, Wallet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileInput } from '@/components/ui/MobileInput';
import { MobileNumberInput } from '@/components/ui/MobileNumberInput';
import { MobileTextarea } from '@/components/ui/MobileTextarea';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { cn } from '@/lib/utils';

import { PartyPicker } from '@/components/pickers/PartyPicker';
import { AccountPicker } from './components/AccountPicker';
import { ConfirmPaymentSheet } from './components/ConfirmPaymentSheet';
import {
    OutstandingInvoicesList,
    type AllocationRow,
} from './components/OutstandingInvoicesList';
import { useAuthStore } from '@/app/store/authStore';
import { recordPaymentIn } from '@/app/services/paymentService';
import { getPartyById } from '@/app/services/partyService';
import type { Party } from '@/app/types/party';
import type { Account } from '@/app/types/account';
import type { PaymentReceivedMultiInvoiceCreate } from '@/app/types/payment';

function todayIso(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

// Used when the Date section is re-enabled:
// function formatDateLabel(iso: string): string {
//     try {
//         return format(new Date(iso), 'dd MMM yyyy');
//     } catch {
//         return iso;
//     }
// }

function toNumber(value: string): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function iconForAccount(typeId: number) {
    if (typeId === 1) return Banknote;
    if (typeId === 2) return Building2;
    return Wallet;
}

export default function NewPaymentInPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);
    const userSiteId = user?.sites?.[0]?.id ?? null;

    const [party, setParty] = useState<Party | null>(null);
    const [account, setAccount] = useState<Account | null>(null);
    const [amount, setAmount] = useState('');
    // Re-add setters (setTransactionDate / setIsAdvance) when the Date / Advance UI is re-enabled.
    const [transactionDate] = useState(todayIso());
    const [isAdvance] = useState(false);
    const [referenceNumber, setReferenceNumber] = useState('');
    const [narration, setNarration] = useState('');
    const [allocations, setAllocations] = useState<AllocationRow[]>([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const [partyPickerOpen, setPartyPickerOpen] = useState(false);
    const [accountPickerOpen, setAccountPickerOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Used when the Date section is re-enabled:
    // const dateInputRef = useRef<HTMLInputElement>(null);

    // Preselect party from URL param
    const prefillPartyId = searchParams.get('party_id');
    useEffect(() => {
        if (!prefillPartyId) return;
        const id = Number(prefillPartyId);
        if (!Number.isFinite(id) || id <= 0) return;
        let cancelled = false;
        getPartyById(id)
            .then((p) => {
                if (!cancelled) setParty(p);
            })
            .catch(() => {
                /* ignore */
            });
        return () => {
            cancelled = true;
        };
    }, [prefillPartyId]);

    const amountNumber = toNumber(amount);
    const totalAllocated = useMemo(
        () =>
            allocations.reduce(
                (sum, a) => (a.selected ? sum + toNumber(a.amount_to_allocate) : sum),
                0
            ),
        [allocations]
    );

    const overAllocated = !isAdvance && totalAllocated > amountNumber + 0.001;
    const hasAtLeastOneAllocation =
        isAdvance ||
        allocations.some((a) => a.selected && toNumber(a.amount_to_allocate) > 0);

    const canSubmit =
        !!party &&
        !!account &&
        amountNumber > 0 &&
        !!transactionDate &&
        hasAtLeastOneAllocation &&
        !overAllocated;

    const mutation = useMutation({
        mutationFn: recordPaymentIn,
        onSuccess: (voucher) => {
            toast.success(
                voucher?.voucher_number
                    ? `Payment ${voucher.voucher_number} recorded`
                    : 'Payment recorded'
            );
            setConfirmOpen(false);
            if (party) {
                queryClient.invalidateQueries({
                    queryKey: ['outstandingInvoices', party.id],
                });
                queryClient.invalidateQueries({ queryKey: ['party', String(party.id)] });
                queryClient.invalidateQueries({
                    queryKey: ['party-recent-orders', String(party.id)],
                });
            }
            queryClient.invalidateQueries({ queryKey: ['parties'] });
            if (prefillPartyId) {
                navigate(`/parties/${prefillPartyId}`, { replace: true });
            } else {
                navigate(-1);
            }
        },
        onError: (err: unknown) => {
            let message = 'Failed to record payment';
            if (axios.isAxiosError(err)) {
                message = err.response?.data?.detail || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }
            toast.error(message);
        },
    });

    const handleReviewClick = () => {
        setSubmitAttempted(true);
        if (!canSubmit) return;
        setConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (!party || !account) return;

        const filteredAllocations = isAdvance
            ? []
            : allocations
                  .filter((a) => a.selected && toNumber(a.amount_to_allocate) > 0)
                  .map((a) => ({
                      invoice_voucher_id: a.invoice_voucher_id,
                      amount_to_allocate: Number(toNumber(a.amount_to_allocate).toFixed(2)),
                  }));

        const payload: PaymentReceivedMultiInvoiceCreate = {
            party_id: party.id,
            received_in_account_id: account.id,
            total_payment_amount: Number(amountNumber.toFixed(2)),
            transaction_date: transactionDate,
            allocations: filteredAllocations,
            reference_number: referenceNumber.trim() || undefined,
            narration: narration.trim() || undefined,
            site_id: userSiteId ?? undefined,
        };

        mutation.mutate(payload);
    };

    const AccountIcon = account ? iconForAccount(account.account_type_id) : Wallet;
    const partyOutstanding = party
        ? Number(party.unallocated_credit || 0)
        : 0;

    const showInvoices = !!party && !isAdvance;

    return (
        <div
            className="flex flex-col bg-background"
            style={{ minHeight: '100dvh', height: '100dvh' }}
        >
            <MobileHeader title="Record Payment" showBack />

            <div
                className="flex-1 overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch' }}
            >
                <div className="p-4 space-y-6 pb-40">
                    {/* Customer */}
                    <section>
                        <h2 className="mobile-section-header">Customer</h2>
                        <MobileCardGroup>
                            <MobileCardRow
                                onClick={() => setPartyPickerOpen(true)}
                                showArrow
                            >
                                {party ? (
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <span className="text-base font-semibold text-primary">
                                                {party.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-foreground truncate">
                                                {party.name}
                                            </p>
                                            {partyOutstanding > 0 && (
                                                <p className="text-xs text-green-500">
                                                    Available credit ₹
                                                    {partyOutstanding.toLocaleString('en-IN')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                            <User className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <span className="text-muted-foreground">
                                            Select customer
                                        </span>
                                    </div>
                                )}
                            </MobileCardRow>
                        </MobileCardGroup>
                        {submitAttempted && !party && (
                            <p className="text-xs text-destructive mt-1 px-1">
                                Customer is required
                            </p>
                        )}
                    </section>

                    {/* Received In */}
                    <section>
                        <h2 className="mobile-section-header">Received In</h2>
                        <MobileCardGroup>
                            <MobileCardRow
                                onClick={() => setAccountPickerOpen(true)}
                                showArrow
                            >
                                {account ? (
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <AccountIcon className="h-5 w-5 text-primary" />
                                        </div>
                                        <p className="font-semibold text-foreground truncate">
                                            {account.name}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                            <Wallet className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <span className="text-muted-foreground">
                                            Select account
                                        </span>
                                    </div>
                                )}
                            </MobileCardRow>
                        </MobileCardGroup>
                        {submitAttempted && !account && (
                            <p className="text-xs text-destructive mt-1 px-1">
                                Account is required
                            </p>
                        )}
                    </section>

                    {/* Amount */}
                    <section>
                        <h2 className="mobile-section-header">Amount</h2>
                        <MobileNumberInput
                            prefix={<span className="text-xl">₹</span>}
                            placeholder="0.00"
                            value={amount}
                            onValueChange={setAmount}
                            sizeVariant="large"
                            autoFocus={!!party}
                            error={submitAttempted && amountNumber <= 0}
                        />
                        {submitAttempted && amountNumber <= 0 && (
                            <p className="text-xs text-destructive mt-1 px-1">
                                Enter an amount greater than 0
                            </p>
                        )}
                    </section>

                    {/*
                        Date section hidden for now (default = today, still sent in payload).
                        Re-enable by uncommenting this section plus the related imports
                        (useRef, format, CalendarIcon, ChevronRight) and formatDateLabel().

                        <section>
                            <h2 className="mobile-section-header">Date</h2>
                            <MobileCardGroup>
                                <button
                                    type="button"
                                    onClick={() => {
                                        dateInputRef.current?.showPicker?.();
                                        dateInputRef.current?.focus();
                                    }}
                                    className="w-full flex items-center justify-between p-4 min-h-[56px] text-left active:bg-accent"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                            <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <p className="font-medium">
                                            {formatDateLabel(transactionDate)}
                                        </p>
                                    </div>
                                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                    <input
                                        ref={dateInputRef}
                                        type="date"
                                        value={transactionDate}
                                        onChange={(e) => setTransactionDate(e.target.value)}
                                        className="sr-only"
                                        aria-label="Transaction date"
                                    />
                                </button>
                            </MobileCardGroup>
                        </section>
                    */}

                    {/*
                        Advance-payment toggle hidden for now (default = false).
                        Re-enable by uncommenting this section and the MobileSwitch import.

                        <section>
                            <MobileCardGroup>
                                <div className="flex items-center justify-between gap-3 p-4 min-h-[56px]">
                                    <div className="min-w-0">
                                        <p className="font-medium text-foreground">
                                            Advance payment
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Skip invoice allocation and record as credit
                                        </p>
                                    </div>
                                    <MobileSwitch
                                        checked={isAdvance}
                                        onCheckedChange={setIsAdvance}
                                    />
                                </div>
                            </MobileCardGroup>
                        </section>
                    */}

                    {/* Outstanding invoices */}
                    {showInvoices && party && (
                        <section>
                            <h2 className="mobile-section-header">Allocate to invoices</h2>
                            <OutstandingInvoicesList
                                partyId={party.id}
                                paymentAmount={amountNumber}
                                allocations={allocations}
                                onChange={setAllocations}
                            />
                            {submitAttempted && overAllocated && (
                                <p className="text-xs text-destructive mt-2 px-1">
                                    Allocated amount exceeds payment total
                                </p>
                            )}
                            {submitAttempted && !hasAtLeastOneAllocation && amountNumber > 0 && (
                                <p className="text-xs text-destructive mt-2 px-1">
                                    Allocate to at least one invoice or enable Advance payment
                                </p>
                            )}
                        </section>
                    )}

                    {/* Reference */}
                    <section>
                        <h2 className="mobile-section-header">Reference # (optional)</h2>
                        <MobileInput
                            placeholder="Cheque / UPI / transaction ID"
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            maxLength={50}
                            autoCapitalize="off"
                        />
                    </section>

                    {/* Notes */}
                    <section>
                        <h2 className="mobile-section-header">Notes (optional)</h2>
                        <MobileTextarea
                            placeholder="Any narration..."
                            value={narration}
                            onChange={(e) => setNarration(e.target.value)}
                        />
                    </section>
                </div>
            </div>

            {/* Sticky submit */}
            <div
                className="border-t border-border bg-background"
                style={{
                    paddingTop: 12,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
                }}
            >
                <MobileButton
                    type="button"
                    size="lg"
                    disabled={!canSubmit || mutation.isPending}
                    onClick={handleReviewClick}
                    className={cn('w-full')}
                >
                    {amountNumber > 0
                        ? `Review ₹${amountNumber.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                        : 'Review Payment'}
                </MobileButton>
            </div>

            <PartyPicker
                open={partyPickerOpen}
                onClose={() => setPartyPickerOpen(false)}
                onSelect={(p) => {
                    setParty(p);
                    setAllocations([]);
                }}
                selectedPartyId={party?.id ?? null}
            />

            <AccountPicker
                open={accountPickerOpen}
                onClose={() => setAccountPickerOpen(false)}
                onSelect={(a) => setAccount(a)}
                selectedAccountId={account?.id ?? null}
            />

            <ConfirmPaymentSheet
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
                isSubmitting={mutation.isPending}
                party={party}
                account={account}
                amount={amountNumber}
                allocations={allocations}
                referenceNumber={referenceNumber}
                narration={narration}
            />
        </div>
    );
}
